// BulkUploadModal.js - Toplu Öğrenci Yükleme Modalı
import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import axiosConfig from "../../../../api/axiosConfig";

const BulkUploadModal = ({ isOpen, onClose, onSuccess, companies }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [step, setStep] = useState(1); // 1: Dosya Seç, 2: Kolon Eşleştir, 3: Önizleme, 4: Sonuç

  // Veritabanı alanları
  const dbFields = [
    { key: 'name', label: 'Ad Soyad *', required: true },
    { key: 'email', label: 'Email' },
    { key: 'parent_name', label: 'Veli Adı' },
    { key: 'parent_phone', label: 'Veli Telefon' },
    { key: 'phone', label: 'Telefon' },
    { key: 'il', label: 'İl' },
    { key: 'ilce', label: 'İlçe' },
    { key: 'city', label: 'Şehir (Eski Alan)' },
    { key: 'sinif', label: 'Sınıf' },
    { key: 'okul', label: 'Okul' },
    { key: 'registration_date', label: 'Kayıt Tarihi' },
    { key: 'class_type', label: 'Ders Türü' },
    { key: 'password', label: 'Parola' },
    { key: 'aciklama', label: 'Açıklama' },
    { key: 'representative', label: 'Temsilci' },
    { key: 'registered_system', label: 'Şirket ID' },
    { key: 'status', label: 'Durum (1: Aktif, 0: Pasif)' },
    { key: 'registration_status', label: 'Kayıt Durumu' }
  ];

  // Dosya seçildiğinde
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(fileExtension)) {
      alert('Lütfen Excel (.xlsx, .xls) veya CSV dosyası seçin!');
      return;
    }

    setFile(selectedFile);
    parseFile(selectedFile);
  };

  // Dosyayı parse et
  const parseFile = (file) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        
        if (jsonData.length === 0) {
          alert('Dosya boş!');
          return;
        }

        // İlk satır başlıklar
        const headers = jsonData[0];
        setColumns(headers);

        // Veri satırları
        const rows = jsonData.slice(1).filter(row => row.some(cell => cell !== undefined && cell !== ''));
        
        // Verileri obje formatına çevir
        const parsedRows = rows.map((row, index) => {
          const obj = { _rowNumber: index + 2 }; // Excel'de 2. satırdan başlar
          headers.forEach((header, i) => {
            obj[header] = row[i] || '';
          });
          return obj;
        });

        setParsedData(parsedRows);
        
        // Otomatik kolon eşleştirme
        autoMapColumns(headers);
        
        setStep(2);
      } catch (error) {
        console.error('Dosya parse hatası:', error);
        alert('Dosya okunurken hata oluştu!');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Otomatik kolon eşleştirme
  const autoMapColumns = (headers) => {
    const mapping = {};
    
    headers.forEach(header => {
      const lowerHeader = header.toLowerCase().replace(/\s+/g, '');
      
      // Otomatik eşleştirme kuralları
      if (lowerHeader.includes('ad') && lowerHeader.includes('soyad')) {
        mapping.name = header;
      } else if (lowerHeader.includes('isim') || lowerHeader === 'ad') {
        mapping.name = header;
      } else if (lowerHeader.includes('email') || lowerHeader.includes('eposta')) {
        mapping.email = header;
      } else if (lowerHeader.includes('veli') && (lowerHeader.includes('ad') || lowerHeader.includes('isim'))) {
        mapping.parent_name = header;
      } else if (lowerHeader.includes('veli') && (lowerHeader.includes('tel') || lowerHeader.includes('telefon'))) {
        mapping.parent_phone = header;
      } else if (lowerHeader === 'telefon' || lowerHeader === 'tel') {
        mapping.phone = header;
      } else if (lowerHeader === 'il' || lowerHeader === 'İl') {
        mapping.il = header;
      } else if (lowerHeader.includes('ilçe') || lowerHeader.includes('ilce')) {
        mapping.ilce = header;
      } else if (lowerHeader.includes('şehir') || lowerHeader.includes('sehir')) {
        mapping.city = header;
      } else if (lowerHeader.includes('sınıf') || lowerHeader.includes('sinif')) {
        mapping.sinif = header;
      } else if (lowerHeader.includes('okul')) {
        mapping.okul = header;
      } else if (lowerHeader.includes('parola') || lowerHeader.includes('şifre') || lowerHeader.includes('sifre')) {
        mapping.password = header;
      } else if (lowerHeader.includes('açıklama') || lowerHeader.includes('aciklama') || lowerHeader.includes('not')) {
        mapping.aciklama = header;
      }
    });

    setColumnMapping(mapping);
  };

  // Kolon eşleştirme değişikliği
  const handleMappingChange = (dbField, excelColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [dbField]: excelColumn || undefined
    }));
  };

  // Veri önizleme ve doğrulama
  const prepareDataForUpload = () => {
    const preparedData = [];
    const validationErrors = [];

    parsedData.forEach((row, index) => {
      const student = {};
      let hasError = false;

      // Her DB alanı için veriyi al
      dbFields.forEach(field => {
        const excelColumn = columnMapping[field.key];
        if (excelColumn) {
          let value = row[excelColumn];
          
          // Veri temizleme ve dönüştürme
          if (field.key === 'status') {
            value = value === '0' || value === 0 || value === 'Pasif' ? 0 : 1;
          } else if (field.key === 'registration_status') {
            value = parseInt(value) || 0;
          } else if (field.key === 'sinif') {
            value = value ? value.toString() : '';
          } else if (field.key === 'registered_system') {
            // Şirket adından ID bul
            if (companies && value) {
              const company = companies.find(c => c.sirket_adi === value);
              value = company ? company.id : value;
            }
          }
          
          student[field.key] = value || '';
        } else if (field.required) {
          hasError = true;
          validationErrors.push({
            row: row._rowNumber,
            field: field.label,
            message: `${field.label} alanı zorunludur`
          });
        }
      });

      // Zorunlu alan kontrolü
      if (!student.name || student.name.trim() === '') {
        hasError = true;
        validationErrors.push({
          row: row._rowNumber,
          field: 'Ad Soyad',
          message: 'Ad Soyad boş olamaz'
        });
      }

      if (!hasError) {
        preparedData.push(student);
      }
    });

    setErrors(validationErrors);
    return preparedData;
  };

  // Önizleme adımına geç
  const handleProceedToPreview = () => {
    const data = prepareDataForUpload();
    if (data.length === 0) {
      alert('Yüklenecek geçerli veri bulunamadı!');
      return;
    }
    setStep(3);
  };

  // Verileri yükle
  const handleUpload = async () => {
    const dataToUpload = prepareDataForUpload();
    
    if (dataToUpload.length === 0) {
      alert('Yüklenecek veri yok!');
      return;
    }

    setUploading(true);
    
    try {
      const response = await axiosConfig.post('/admin/ogrencis/bulk', {
        students: dataToUpload
      });

      setUploadResults(response.data.results);
      setStep(4);
      
      if (response.data.results.success.length > 0) {
        onSuccess();
      }
    } catch (error) {
      console.error('Yükleme hatası:', error);
      alert('Yükleme sırasında hata oluştu!');
    } finally {
      setUploading(false);
    }
  };

  // Modal'ı kapat ve sıfırla
  const handleClose = () => {
    setFile(null);
    setParsedData([]);
    setColumns([]);
    setColumnMapping({});
    setErrors([]);
    setUploadResults(null);
    setStep(1);
    onClose();
  };

  // Örnek Excel dosyası indir
  const downloadSampleFile = () => {
    const sampleData = [
      {
        'Ad Soyad': 'Örnek Öğrenci 1',
        'Email': 'ornek1@email.com',
        'Veli Adı': 'Veli Adı 1',
        'Veli Telefon': '5551234567',
        'Telefon': '5559876543',
        'İl': 'İstanbul',
        'İlçe': 'Kadıköy',
        'Sınıf': '5',
        'Okul': 'Örnek Okul',
        'Parola': '123456',
        'Açıklama': 'Test öğrenci'
      },
      {
        'Ad Soyad': 'Örnek Öğrenci 2',
        'Email': 'ornek2@email.com',
        'Veli Adı': 'Veli Adı 2',
        'Veli Telefon': '5551234568',
        'Telefon': '5559876544',
        'İl': 'Ankara',
        'İlçe': 'Çankaya',
        'Sınıf': '6',
        'Okul': 'Örnek Okul 2',
        'Parola': '123456',
        'Açıklama': 'Test öğrenci 2'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Öğrenciler');
    XLSX.writeFile(wb, 'ornek_ogrenci_listesi.xlsx');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* Başlık */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-white">
              Toplu Öğrenci Yükleme
            </h3>
            <div className="flex items-center gap-4">
              <span className="text-white text-sm">
                Adım {step} / 4
              </span>
              <button
                onClick={handleClose}
                className="text-white hover:text-gray-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* İçerik */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Adım 1: Dosya Seçimi */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">📋 Kullanım Talimatları</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Excel (.xlsx, .xls) veya CSV dosyası yükleyebilirsiniz</li>
                  <li>• İlk satır başlık satırı olmalıdır</li>
                  <li>• Ad Soyad alanı zorunludur</li>
                  <li>• İl ve İlçe isimleri tam yazılmalıdır (örn: İstanbul, Kadıköy)</li>
                  <li>• Parola belirtilmezse varsayılan "123456" olacaktır</li>
                  <li>• Telefon numaraları otomatik formatlanacaktır</li>
                </ul>
              </div>

              <div className="text-center">
                <button
                  onClick={downloadSampleFile}
                  className="mb-4 text-indigo-600 hover:text-indigo-700 underline text-sm"
                >
                  📥 Örnek Excel Dosyasını İndir
                </button>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                
                <label className="cursor-pointer">
                  <span className="mt-2 block text-sm font-medium text-gray-900">
                    Dosya seçmek için tıklayın
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                  />
                </label>
                
                {file && (
                  <div className="mt-4 text-sm text-gray-600">
                    Seçilen dosya: <span className="font-semibold">{file.name}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Adım 2: Kolon Eşleştirme */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">🔗 Kolon Eşleştirme</h4>
                <p className="text-sm text-yellow-800">
                  Excel dosyanızdaki kolonları sistem alanlarıyla eşleştirin. 
                  Sistem otomatik eşleştirme yapmıştır, kontrol edip düzeltebilirsiniz.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {dbFields.map(field => (
                  <div key={field.key} className="flex items-center gap-2">
                    <label className="w-1/2 text-sm font-medium text-gray-700">
                      {field.label}
                    </label>
                    <select
                      className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      value={columnMapping[field.key] || ''}
                      onChange={(e) => handleMappingChange(field.key, e.target.value)}
                    >
                      <option value="">-- Seçiniz --</option>
                      {columns.map(col => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  ← Geri
                </button>
                <button
                  onClick={handleProceedToPreview}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Önizleme →
                </button>
              </div>
            </div>
          )}

          {/* Adım 3: Önizleme */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">✅ Veri Önizleme</h4>
                <p className="text-sm text-green-800">
                  {parsedData.length} kayıt bulundu. 
                  {errors.length > 0 && ` ${errors.length} hata tespit edildi.`}
                </p>
              </div>

              {/* Hatalar */}
              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-2">❌ Hatalar</h4>
                  <div className="max-h-32 overflow-y-auto">
                    {errors.slice(0, 10).map((error, index) => (
                      <div key={index} className="text-sm text-red-700">
                        Satır {error.row}: {error.message}
                      </div>
                    ))}
                    {errors.length > 10 && (
                      <div className="text-sm text-red-700 font-semibold mt-2">
                        ... ve {errors.length - 10} hata daha
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Veri tablosu */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ad Soyad</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Veli Telefon</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">İl</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">İlçe</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sınıf</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Okul</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {parsedData.slice(0, 10).map((row, index) => {
                      const student = {};
                      dbFields.forEach(field => {
                        const excelColumn = columnMapping[field.key];
                        if (excelColumn) {
                          student[field.key] = row[excelColumn];
                        }
                      });
                      
                      return (
                        <tr key={index}>
                          <td className="px-3 py-2 text-sm text-gray-900">{index + 1}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{student.name || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{student.email || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{student.parent_phone || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{student.il || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{student.ilce || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{student.sinif || '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900">{student.okul || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {parsedData.length > 10 && (
                  <div className="text-center py-2 text-sm text-gray-500">
                    ... ve {parsedData.length - 10} kayıt daha
                  </div>
                )}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep(2)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  ← Geri
                </button>
                <button
                  onClick={handleUpload}
                  disabled={uploading || errors.length === parsedData.length}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Yükleniyor...' : `${parsedData.length - errors.length} Kaydı Yükle`}
                </button>
              </div>
            </div>
          )}

          {/* Adım 4: Sonuç */}
          {step === 4 && uploadResults && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">📊 Yükleme Sonucu</h4>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{uploadResults.success.length}</div>
                    <div className="text-sm text-gray-600">Başarılı</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-600">{uploadResults.errors.length}</div>
                    <div className="text-sm text-gray-600">Hata</div>
                  </div>
                </div>
              </div>

              {/* Başarılı kayıtlar */}
              {uploadResults.success.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-semibold text-green-900 mb-2">✅ Başarılı Kayıtlar</h4>
                  <div className="max-h-40 overflow-y-auto">
                    {uploadResults.success.map((item, index) => (
                      <div key={index} className="text-sm text-green-700">
                        Satır {item.row}: {item.name} (ID: {item.id})
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hatalı kayıtlar */}
              {uploadResults.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-semibold text-red-900 mb-2">❌ Hatalı Kayıtlar</h4>
                  <div className="max-h-40 overflow-y-auto">
                    {uploadResults.errors.map((item, index) => (
                      <div key={index} className="text-sm text-red-700">
                        Satır {item.row}: {item.name} - {item.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-center">
                <button
                  onClick={handleClose}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Kapat
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
